import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import api from '../../services/api';

// Reemplaza a window.confirm (/apple-design, 5-oct-2026): título concreto, botón con
// el verbo de la acción (no "Aceptar"), Cancelar enfocado en las destructivas, Escape
// y clic fuera cancelan, el foco queda atrapado y vuelve a donde estaba. Con
// `checkCalls` avisa si hay llamadas activas (Principio #2 del producto); si la
// consulta falla no bloquea (mismo criterio fail-open de callSafety.js).
//
//   const confirm = useConfirm();
//   if (await confirm({ title: 'Eliminar la cola Ventas', danger: true, confirmLabel: 'Eliminar' })) ...

const ConfirmContext = createContext(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm requiere <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [dlg, setDlg] = useState(null); // { opts, resolve, calls }
  const returnFocus = useRef(null);

  const confirm = useCallback((opts) => new Promise((resolve) => {
    returnFocus.current = document.activeElement;
    setDlg({ opts, resolve, calls: null });
    if (opts.checkCalls) {
      api.get('/dashboard/stats')
        .then((r) => setDlg((d) => (d && d.resolve === resolve ? { ...d, calls: r.data?.calls?.active ?? 0 } : d)))
        .catch(() => {});
    }
  }), []);

  const close = useCallback((ok) => {
    setDlg((d) => { if (d) d.resolve(ok); return null; });
    const el = returnFocus.current;
    if (el && typeof el.focus === 'function') setTimeout(() => el.focus(), 0);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dlg && <Dialog dlg={dlg} close={close} />}
    </ConfirmContext.Provider>
  );
}

function Dialog({ dlg, close }) {
  const { title, message, confirmLabel = 'Confirmar', cancelLabel = 'Cancelar', danger = false } = dlg.opts;
  const boxRef = useRef(null);
  const cancelRef = useRef(null);
  const okRef = useRef(null);

  useEffect(() => { (danger ? cancelRef : okRef).current?.focus(); }, [danger]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(false); return; }
    if (e.key !== 'Tab') return;
    const f = boxRef.current.querySelectorAll('button:not([disabled])');
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return (
    <div role="presentation" className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) close(false); }} onKeyDown={onKeyDown}>
      <div ref={boxRef} role="alertdialog" aria-modal="true" aria-labelledby="confirm-title"
           aria-describedby={message ? 'confirm-msg' : undefined}
           className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md p-6 shadow-2xl">
        <h2 id="confirm-title" className="text-lg font-semibold text-white">{title}</h2>
        {message && <p id="confirm-msg" className="mt-2 text-sm text-gray-300 whitespace-pre-line">{message}</p>}
        {dlg.calls > 0 && (
          <div role="alert" className="mt-3 flex items-start gap-2 rounded-lg bg-yellow-900/30 border border-yellow-800 px-3 py-2 text-sm text-yellow-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>Hay {dlg.calls} llamada{dlg.calls !== 1 ? 's' : ''} activa{dlg.calls !== 1 ? 's' : ''} en el sistema. Esta acción podría afectarlas.</span>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button ref={cancelRef} type="button" className="btn-secondary" onClick={() => close(false)}>{cancelLabel}</button>
          <button ref={okRef} type="button" className={danger ? 'btn-danger' : 'btn-primary'} onClick={() => close(true)}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
